import { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { initGA } from '../analytics';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be changed
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a small delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(prev => ({...prev, ...saved}));
        applyConsent(saved);
      } catch (error) {
        console.error('Error loading cookie preferences:', error);
      }
    }
  }, []);

  const applyConsent = (consent) => {
    // Apply Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': consent.analytics ? 'granted' : 'denied',
        'ad_storage': consent.advertising ? 'granted' : 'denied',
      });
    }

    // You can add more consent implementations here for other services
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent(prefs);
    
    // Initialize Google Analytics if analytics consent is given
    if (prefs.analytics) {
      initGA();
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertising: true
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      advertising: false
    };
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {!showSettings ? (
          // Main Banner
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Cookie className="w-8 h-8 text-blue-900" />
                <h2 className="text-2xl font-bold text-slate-900">Uso de Cookies</h2>
              </div>
              <button
                onClick={acceptNecessary}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Cerrar"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-slate-700 leading-relaxed mb-4">
              Utilizamos cookies y tecnologías similares para mejorar su experiencia en JobSuiza, 
              analizar el uso del sitio y personalizar la publicidad.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-700">
                <strong>Cookies necesarias:</strong> Esenciales para el funcionamiento del sitio (autenticación, sesión). 
                Siempre activas.
              </p>
              <p className="text-sm text-slate-700 mt-2">
                <strong>Cookies opcionales:</strong> Google Analytics (análisis) y Google AdSense (publicidad). 
                Requieren su consentimiento.
              </p>
            </div>

            <p className="text-sm text-slate-600 mb-6">
              Para más información, consulte nuestra{' '}
              <Link to="/cookie-policy" className="text-blue-900 underline hover:text-blue-700" onClick={() => setShowBanner(false)}>
                Política de Cookies
              </Link>
              {' '}y{' '}
              <Link to="/privacy-policy" className="text-blue-900 underline hover:text-blue-700" onClick={() => setShowBanner(false)}>
                Política de Privacidad
              </Link>.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={acceptAll}
                className="flex-1 bg-blue-900 hover:bg-blue-800 text-white"
              >
                Aceptar Todas
              </Button>
              <Button
                onClick={acceptNecessary}
                variant="outline"
                className="flex-1 border-slate-300 hover:bg-slate-50"
              >
                Solo Necesarias
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="flex-1 border-slate-300 hover:bg-slate-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Personalizar
              </Button>
            </div>
          </div>
        ) : (
          // Settings Panel
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-bold text-slate-900">Configuración de Cookies</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Volver"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-slate-600 mb-6">
              Elija qué cookies desea permitir. Las cookies necesarias siempre están activas.
            </p>

            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Cookies Necesarias</h3>
                    <p className="text-sm text-slate-600 mb-2">
                      Esenciales para el funcionamiento del sitio web. No se pueden desactivar.
                    </p>
                    <p className="text-xs text-slate-500">
                      Incluye: Autenticación, sesión, preferencias de cookies
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-blue-900 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 text-center">Siempre</p>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Cookies de Análisis</h3>
                    <p className="text-sm text-slate-600 mb-2">
                      Nos ayudan a entender cómo los visitantes usan el sitio para mejorarlo.
                    </p>
                    <p className="text-xs text-slate-500">
                      Incluye: Google Analytics (_ga, _gid, _gat)
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({...preferences, analytics: !preferences.analytics})}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics ? 'bg-blue-900 justify-end' : 'bg-slate-300 justify-start'
                      } px-1`}
                      aria-label="Toggle analytics cookies"
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                    <p className="text-xs text-slate-500 mt-1 text-center">
                      {preferences.analytics ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Advertising Cookies */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">Cookies de Publicidad</h3>
                    <p className="text-sm text-slate-600 mb-2">
                      Permiten mostrar anuncios relevantes para usted y medir su efectividad.
                    </p>
                    <p className="text-xs text-slate-500">
                      Incluye: Google AdSense (__gads, __gac, IDE)
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({...preferences, advertising: !preferences.advertising})}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.advertising ? 'bg-blue-900 justify-end' : 'bg-slate-300 justify-start'
                      } px-1`}
                      aria-label="Toggle advertising cookies"
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                    <p className="text-xs text-slate-500 mt-1 text-center">
                      {preferences.advertising ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={saveCustom}
                className="flex-1 bg-blue-900 hover:bg-blue-800"
              >
                Guardar Preferencias
              </Button>
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Puede cambiar sus preferencias en cualquier momento desde el footer del sitio.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
